import { mdiAccountOutline, mdiFileDownloadOutline, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Input,
  Label,
  Table,
} from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import './Calibration.scss';
import { toast } from 'react-toastify';
import {
  fetchCalibrationDetail,
  fetchCalibrationHeader,
} from '../../store/apis/calibrationService';
import { tableColumns } from './components/EmployeeListView/constants';
import useTableHook from '../../components/hooks/useTableHook';
import {
  AsyncAutocompleteSelect,
  FilterDropdown,
  TableFilterBubbles,
  TablePagination,
  TableSortDisplayArrow,
  UserAsyncAutocompleteSelect,
} from '../../components/Common';
import { ExportUtil } from '../../utils';
import {
  fetchClassification,
  fetchDelegateApprovalAutoComplete,
  fetchEmployeeSearchAutoComplete,
} from '../../store/apis/autocompleteService';
import NewCalibartionForm from './components/NewCalibrationForm/NewCalibartionForm';
import Subject from './components/Subject/Subject';
import People from './components/People/People';
import actionTargetIcon from '../../assets/images/calibration/actionTargetIcon.svg';
import actionEditIcon from '../../assets/images/calibration/actionEditIcon.svg';
import actionSlashCircleIcon from '../../assets/images/calibration/actionSlashCircleIcon.svg';
import SessionConfirmation from './components/SessionConfirmation/SessionConfirmation';
import DeleteCalibrationModal from './components/DeleteCalibrationModal/DeleteCalibrationModal';
import { Messages } from '../../common/messages';
import { getAuthUser } from '../../utils/auth.util';
import { fetchHRCodeList } from '../../store/apis/commonService';
import ReleaseCalibrationModal from './components/ReleaseCalibrationModal/ReleaseCalibrationModal';
import { showReleaseButton } from '../Performance/common';

function Calibration() {
  document.title = 'Calibration Session | Envision Daikin';
  const navigate = useNavigate();
  const [newCalibrationForm, setNewCalibrationForm] = useState(false);
  const [subjectForm, setSubjectForm] = useState(false);
  const [peopleForm, setPeopleForm] = useState(false);
  const [sessionConfirmation, setSessionConfirmation] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedPeoples, setSelectedPeoples] = useState([]);
  const [isSubjectValidated, setIsSubjectValidated] = useState(false);
  const [selectedCalibration, setSelectedCalibration] = useState();
  const [modalDelete, setModalDelete] = useState(false);
  const [modalRelease, setModalRelease] = useState(false);
  const [sessionIds, setSessionIds] = useState([]);
  const [toDeleteSessionId, setToDeleteSessionId] = useState(null);

  const { companyList } = getAuthUser();

  const { data: calibrationDetail, refetch: refetchCalibration } = useQuery({
    queryKey: ['calibrationDetail', selectedCalibration?.Session_Id],
    queryFn: () =>
      fetchCalibrationDetail({
        sessionId: selectedCalibration?.Session_Id,
        typ: 'DETAIL',
      }),
    enabled: !!selectedCalibration?.Session_Id,
  });

  useEffect(() => {
    setSelectedSubjects([]);
    setSelectedPeoples([]);
  }, []);

  useEffect(() => {
    if (calibrationDetail?.data) {
      setSelectedPeoples([]);
      calibrationDetail?.data?.forEach(item => {
        setSelectedPeoples(prev => [
          ...prev,
          { EmpCd: item?.EmpCd?.trim(), Name: item?.EmpName?.trim() },
        ]);
      });
    }
  }, [calibrationDetail]);

  useEffect(() => {
    if (selectedCalibration && selectedCalibration?.Reviewed_User_Names) {
      setSelectedSubjects([]);
      selectedCalibration?.Reviewed_User_Names?.split(',').forEach(item => {
        const code = item?.trim()?.split(':');
        setSelectedSubjects(prev => [
          ...prev,
          { EmpCd: code?.[0]?.trim(), Name: code?.[1]?.trim() },
        ]);
      });
    }
  }, [selectedCalibration, selectedCalibration?.Reviewed_User_Names]);

  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    externalFilters,
    activeRows,
    selectAllRows,
    handleRowCheck,
    applyFilter,
    columnName,
    setColumnName,
    handleSetExternalFilters,
    applyExternalFilters,
    setPageLoadTrigger,
    tableDataMutation,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    refetch,
    getPageSize,
    setPageSize,
    nextPage,
    prevPage,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'Session_Id',
      sortDirection: 'DESC',
      typ: 'HEADER',
      filters: [],
    },
    fetchFn: fetchCalibrationHeader,
    queryKeyPrefix: 'CalibrationSession',
    columnKeyPrefix: 'CalibrationSessionColumn',
    tableName: 'CalibrationSession',
    onPageLoadTrigger: false,
    defaultExternalFilters: {},
  });

  useEffect(() => {
    setPageRequest(prev => ({
      ...prev,
    }));
  }, [setPageRequest, setPageLoadTrigger]);

  const { data: departmentList } = useQuery({
    queryKey: ['departmentList'],
    queryFn: () => fetchHRCodeList({ type: 'DEPT' }),
    enabled: true,
    keepPreviousData: true,
  });

  const toggleNewCalibrationForm = () => {
    setSelectedCalibration(null);
    setNewCalibrationForm(!newCalibrationForm);
    if (peopleForm) {
      setPeopleForm(!peopleForm);
    }
  };

  const toggleEditCalibrationForm = detail => {
    setSelectedCalibration(detail);
    setNewCalibrationForm(!newCalibrationForm);
    setPeopleForm(true);
  };

  const toggleSubjectForm = subjects => {
    if (subjects && Array.isArray(subjects)) {
      setSelectedSubjects(subjects);
    } else {
      setSelectedSubjects([]);
    }
    setSubjectForm(!subjectForm);
  };

  const togglePeopleForm = (
    peoples,
    subjects,
    isValidated,
    toggleState = false,
  ) => {
    if (peoples) {
      setSelectedPeoples(peoples);
    }
    if (subjects) {
      setSelectedSubjects(subjects);
    }
    if (isValidated) {
      setPeopleForm(toggleState);
      setIsSubjectValidated(true);
      return;
    }
    setIsSubjectValidated(false);
    setPeopleForm(!peopleForm);
  };

  const [pendingCloseOffcanvas, setPendingCloseOffcanvas] = useState(false);

  const toggleConfirmation = action => {
    if (action === 'yes') {
      setSessionConfirmation(false);
      setSubjectForm(false);
      setPeopleForm(false);
      setPendingCloseOffcanvas(true);

      if (!selectedCalibration) {
        setIsSubjectValidated(false);
        setSelectedSubjects([]);
        setSelectedPeoples([]);
      }
      return;
    }
    setSessionConfirmation(prev => !prev);
  };

  useEffect(
    () => () => {
      document.body.classList.remove('modal-open', 'offcanvas-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    },
    [],
  );

  // Export based on filters without pagination
  const handleExportExcel = async () => {
    const exportData = data?.data;

    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);
    const exportResponseData = await tableDataMutation.mutateAsync({
      ...pageRequest,
      pageNumber: 1,
      pageSize: 0,
    });

    const formattedData = exportResponseData?.result?.map(item => ({
      Session_Id: item?.Session_Id,
      Session_Name: item?.Session_Name,
      NoPeople: item?.NoPeople,
      Session_Status: item?.Session_Status,
      Session_Date: item?.Session_Date
        ? moment(item?.Session_Date).format('DD/MM/YYYY')
        : '-',
    }));

    let excelData = formattedData;
    if (activeRows?.length > 0) {
      excelData =
        formattedData?.filter((_item, index) => activeRows?.includes(index)) ||
        [];
    }

    ExportUtil.downloadExcel(excelData, {
      fileName: `Calibration_${timesTamp}.xlsx`,
      columns: tableColumns,
    });
  };

  // Export based on filters without pagination
  const handleExportExcelAll = async () => {
    const exportData = data?.data;

    if (!exportData || exportData.length === 0) {
      toast.error('No data found to export', {
        className: 'toast-error',
      });
      return;
    }
    const timesTamp = new Date().toISOString().slice(0, 10);
    const exportResponseData = await tableDataMutation.mutateAsync({
      ...pageRequest,
      pageNumber: 1,
      pageSize: 0,
    });

    const formattedData = exportResponseData?.result?.map(item => ({
      Session_Id: item?.Session_Id,
      Session_Name: item?.Session_Name,
      NoPeople: item?.NoPeople,
      Session_Status: item?.Session_Status,
      Session_Date: item?.Session_Date
        ? moment(item?.Session_Date).format('DD/MM/YYYY')
        : '-',
    }));

    ExportUtil.downloadExcel(formattedData, {
      fileName: `Calibration_${timesTamp}.xlsx`,
      columns: tableColumns,
    });
  };

  const refetchCalibrationDetails = () => {
    refetch();
    refetchCalibration();
    setSelectedPeoples([]);
    setSelectedSubjects([]);
  };

  useEffect(() => {
    refetchCalibrationDetails();
    setPageLoadTrigger(true);
  }, []);

  const toggleDelete = detail => {
    setToDeleteSessionId(detail?.Session_Id);
    setModalDelete(!modalDelete);
  };

  const handleReleaseResult = () => {
    const apiResult = data?.data;
    if (!apiResult || apiResult.length === 0) {
      toast.error('No data found to release results', {
        className: 'toast-error',
      });
      return;
    }
    const nonCompleted = apiResult?.filter(
      item =>
        ['Completed', 'Cancelled', 'Released']?.includes(
          item.Session_Status,
        ) === false,
    );
    if (nonCompleted?.length > 0) {
      toast.error(
        'All calibration sessions are not yet completed. Please ensure they are finished before releasing.',
        {
          className: 'toast-error',
        },
      );
      return;
    }
    const tobeReleasedIds = apiResult?.map(item => item.Session_Id).join(',');
    setSessionIds(tobeReleasedIds);
    setModalRelease(true);
  };

  const renderTableBody = () => {
    const tableData = Array.isArray(data) ? data : data?.data || [];
    // Ensure tableData is not undefined or null
    if (!tableData || tableData.length === 0) {
      if (data?.isLoading) {
        return (
          <tr>
            <td className="text-center" colSpan={7}>
              Loading...
            </td>
          </tr>
        );
      }
      return (
        <tr>
          <td className="text-center" colSpan={7}>
            {Messages.NO_CALIBRATION}
          </td>
        </tr>
      );
    }
    return tableData.map((item, index) => (
      <tr key={item?.Session_Id}>
        <td className="py-2">
          <div className="form-check px-0">
            <Input
              className="form-check-input blue-checkbox"
              type="checkbox"
              name="chk_child"
              checked={activeRows?.includes(index)}
              onChange={e => handleRowCheck(index, e.target.checked)}
            />
          </div>
        </td>
        <td className="px-2">{item.Session_Id}</td>
        <td className="px-2">{item.Session_Name}</td>
        <td className="px-2">{item.NoPeople}</td>
        <td className="px-2">{item.Session_Status}</td>
        <td className="px-2 ">{moment(item.Session_Date).format('L')}</td>
        <td className="px-2 text-center">
          <div className="d-inline-flex align-items-center gap-1 justify-content-center">
            <Button
              size="sm"
              className="non-btn-cb"
              color="link"
              title="Calibrate"
              onClick={() =>
                navigate(`/hr/performance-calibration/${item.Session_Id}`)
              }
            >
              <img src={actionTargetIcon} alt="target" width={20} height={20} />
            </Button>
            {!['Completed', 'Cancelled', 'Released']?.includes(
              item.Session_Status,
            ) && (
              <>
                <Button
                  size="sm"
                  className="non-btn-cb"
                  color="link"
                  title="Edit"
                  onClick={() => toggleEditCalibrationForm(item)}
                >
                  <img src={actionEditIcon} alt="edit" width={20} height={20} />
                </Button>
                <Button
                  size="sm"
                  className="non-btn-cb"
                  color="link"
                  title="Cancel"
                  onClick={() => toggleDelete(item)}
                >
                  <img
                    src={actionSlashCircleIcon}
                    alt="action"
                    width={20}
                    height={20}
                  />
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>
    ));
  };

  return (
    <div className="page-content main-table-page">
      <Card className="cb-card enquiry-card">
        <CardHeader
          className="w-100"
          style={{ borderRadius: '10px 10px 0 0', borderBottom: '0' }}
        >
          <div className="d-flex flex-row align-items-center justify-content-between w-100">
            <div className="fw-semibold fs-5">Calibration Session</div>
            <div className="d-flex flex-row align-items-center gap-2">
              <div className="d-flex flex-row align-items-center">
                <button
                  onClick={handleExportExcel}
                  type="button"
                  className="cs-download-btn"
                >
                  <span>Export</span>
                  <Icon path={mdiFileDownloadOutline} size={0.8} />
                </button>
                <button
                  onClick={handleExportExcelAll}
                  type="button"
                  className="cs-s-download-btn"
                >
                  <span>Export All</span>
                  <Icon path={mdiFileDownloadOutline} size={0.8} />
                </button>
              </div>
              <div className="d-flex flex-row align-items-center gap-2">
                {showReleaseButton(data?.data) && (
                  <button
                    type="button"
                    className="custom-success-btn"
                    onClick={handleReleaseResult}
                  >
                    <span>Release Result</span>
                  </button>
                )}
                <button
                  onClick={toggleNewCalibrationForm}
                  className="custom-btn"
                  type="button"
                >
                  <Icon path={mdiPlus} size={0.8} />
                  <span>Create New</span>
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0 ">
          <div className="external-filter-calibration gap-3 mx-2 mt-2 mb-3 p-3">
            <div className="filter-row gap-2">
              <div className="external-cb-filter-col">
                <Label className="label">Company</Label>
                <Input
                  type="select"
                  onChange={event =>
                    handleSetExternalFilters(
                      'companyCode',
                      event.target.value.trim(),
                    )
                  }
                  className="external-filter-input"
                >
                  <option value="">All</option>
                  {companyList?.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="external-cb-filter-col">
                <Label className="label">Subject</Label>
                <UserAsyncAutocompleteSelect
                  autoCompleteFn={input =>
                    fetchDelegateApprovalAutoComplete({
                      prefixText: input,
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Cd,
                      label: item.Name,
                      name: item.Name,
                      code: item.Cd,
                      image: item?.Imagefile,
                    }))
                  }
                  value={externalFilters?.subjectId}
                  onChange={selectedOption => {
                    handleSetExternalFilters('subjectId', selectedOption);
                  }}
                  placeholder="All"
                  isClearable
                  isSearchable
                  inputWidth="100%"
                  inputIcon={
                    <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                  }
                />
              </div>
              <div className="external-cb-filter-col">
                <Label className="label">Participant</Label>
                <UserAsyncAutocompleteSelect
                  autoCompleteFn={input =>
                    fetchEmployeeSearchAutoComplete({
                      prefixText: input,
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.EmpCd,
                      label: item.Name,
                      name: item.Name,
                      code: item.EmpCd,
                      image: item?.Imagefile,
                    }))
                  }
                  onChange={selectedOption =>
                    handleSetExternalFilters('participantId', selectedOption)
                  }
                  value={externalFilters?.participantId}
                  placeholder="All"
                  isClearable
                  inputWidth="100%"
                  inputIcon={
                    <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                  }
                />
              </div>
            </div>
            <div className="filter-row gap-2">
              <div className="external-cb-filter-col">
                <Label className="label">Department</Label>
                <Input
                  type="select"
                  onChange={event =>
                    handleSetExternalFilters(
                      'departmentId',
                      event.target.value.trim(),
                    )
                  }
                  className="external-filter-input"
                >
                  <option value="">All</option>
                  {departmentList?.map(type => (
                    <option key={type.Code} value={type.Code}>
                      {type.Description}
                    </option>
                  ))}
                </Input>
              </div>
              <div className="external-cb-filter-col">
                <Label className="label">Classification</Label>
                <AsyncAutocompleteSelect
                  autoCompleteFn={input =>
                    fetchClassification({
                      prefixText: input,
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(item => ({
                      value: item.Cd,
                      label: item.Des,
                    }))
                  }
                  onChange={selectedOption =>
                    handleSetExternalFilters(
                      'classificationId',
                      selectedOption?.value.trim(),
                    )
                  }
                  placeholder="All"
                  isClearable
                  displayOption="bothCustom"
                  inputWidth="100%"
                  inputIcon={
                    <Icon path={mdiAccountOutline} size={0.8} color="#667085" />
                  }
                />
              </div>
              <div className="external-cb-filter-col">
                <Label className="label">Status</Label>
                <Input
                  type="select"
                  className="form-control external-filter-input"
                  value={externalFilters.approvalType}
                  onChange={e =>
                    handleSetExternalFilters('status', e.target.value)
                  }
                >
                  <option value="">All</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Cancelled">Cancelled</option>
                  <option value="Completed">Completed</option>
                  <option value="In-Progress">In-Progress</option>
                  <option value="Released">Released</option>
                </Input>
              </div>
            </div>
            <div className="filter-action">
              <button
                type="button"
                className="search-calibration-btn justify-center custom-btn"
                onClick={() =>
                  applyExternalFilters({
                    ...externalFilters,
                    subjectId: externalFilters?.subjectId?.value || null,
                    participantId:
                      externalFilters?.participantId?.value || null,
                  })
                }
              >
                Search
              </button>
            </div>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={tableColumns}
            />
          </div>
          <div className="table-responsive calibration table-container">
            <Table
              hover
              className="custom-table align-middle text-nowrap"
              id="calibration-table"
            >
              <thead>
                <tr>
                  <th>
                    <div className="form-check px-0">
                      <Input
                        className="form-check-input blue-checkbox"
                        type="checkbox"
                        id="checkbox"
                        checked={
                          activeRows?.length > 0 &&
                          activeRows?.length === data?.data?.length
                        }
                        onChange={e => selectAllRows(e?.target?.checked)}
                      />
                    </div>
                  </th>
                  {Object.entries(tableColumns).map(([key, value]) => (
                    <th key={key} className="px-2">
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => handleAutoSort(key)}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                        />
                      </div>
                    </th>
                  ))}
                  <th className="px-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter style={{ borderRadius: '0 0 10px 10px' }} className="py-2">
          <div>
            <TablePagination
              nextPage={nextPage}
              prevPage={prevPage}
              totalPages={data?.totalPages}
              currentPage={data?.currentPage}
              hasPreviousPage={data?.hasPreviousPage}
              hasNextPage={data?.hasNextPage}
              getPageSize={getPageSize}
              setPageSize={setPageSize}
              totalCount={data?.totalCount}
            />
          </div>
        </CardFooter>
      </Card>
      <NewCalibartionForm
        isOpen={newCalibrationForm}
        toggle={toggleNewCalibrationForm}
        refetchCalibrationDetails={refetchCalibrationDetails}
        toggleSubject={toggleSubjectForm}
        togglePeople={togglePeopleForm}
        toggleConfirmation={toggleConfirmation}
        subjects={selectedSubjects}
        peoples={selectedPeoples}
        isSubjectValidated={isSubjectValidated}
        selectedCalibration={selectedCalibration}
      />
      <Subject
        isOpen={subjectForm}
        toggle={toggleSubjectForm}
        initialSelectedSubjects={selectedSubjects}
        peoples={selectedPeoples}
        togglePeople={togglePeopleForm}
      />
      <People
        isOpen={peopleForm}
        subjects={selectedSubjects}
        peoples={selectedPeoples}
        toggle={togglePeopleForm}
        toggleSubjectForm={toggleSubjectForm}
      />
      <SessionConfirmation
        isOpen={sessionConfirmation}
        toggle={toggleConfirmation}
        pendingCloseOffcanvas={pendingCloseOffcanvas}
        setPendingCloseOffcanvas={setPendingCloseOffcanvas}
        setNewCalibrationForm={setNewCalibrationForm}
      />
      <DeleteCalibrationModal
        isOpen={modalDelete}
        toggle={toggleDelete}
        refetchCalibrationDetails={refetchCalibrationDetails}
        toDeleteSessionId={toDeleteSessionId}
        setToDeleteSessionId={setToDeleteSessionId}
      />
      <ReleaseCalibrationModal
        isOpen={modalRelease}
        toggle={() => setModalRelease(false)}
        sessionIds={sessionIds}
        refetchCalibrationDetails={refetchCalibrationDetails}
      />
    </div>
  );
}
export default Calibration;
